<script setup lang="ts">
//#region imports
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import {
  type EmitsToListeners,
  type ErrorResponse,
  type ToErrorMap,
  getErrorMessage,
  typeGuard,
} from '@/types/core';
import type {
  GetMediaBuyingCampaignsByIdUsedWalletsResponse,
  GetMediaBuyingPostsByIdResponse,
  PostMediaBuyingUrlBlacklistsData,
} from '@/types/openapi-ts';
//#endregion

//#region types
type MediaBuyingCampaignsByIdUsedWalletsRow = GetMediaBuyingCampaignsByIdUsedWalletsResponse['payload'][number] & {
  disabled: boolean;
  key: string;
};

type UrlRow = GetMediaBuyingPostsByIdResponse['payload']['urls'][number] & {
  disabled: boolean;
  key: string;
};

type FormErrors = ToErrorMap<PostMediaBuyingUrlBlacklistsData['body']>;

interface Props {
  id: string;
  urls?: GetMediaBuyingPostsByIdResponse['payload']['urls'];
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface PostBlacklistParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  urls: () => [],
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const walletFields = [
  {
    name: 'site_payment_method_name',
    sortable: false,
  },
  {
    name: 'payment_wallet',
    sortable: false,
    class: 'text',
  },
];
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);

const listType = ref<'wallet' | 'url'>('wallet');
const blacklist = ref<MediaBuyingCampaignsByIdUsedWalletsRow[]>([]);
const urlRows = ref<UrlRow[]>([]);
const selected = ref<string[]>([]);
const urlErrors = ref<Record<string, string>>({});
const sortProp = ref('created_at');
const sortOrder = ref('asc');
//#endregion

//#region computed
const typeOptions = computed(() => [
  { label: t('mediaBuying.blacklistWallets.popup.wallet'), value: 'wallet' },
  { label: t('mediaBuying.blacklistWallets.popup.urls'), value: 'url' },
]);
const urlFields = computed(() => [
  {
    name: 'url',
    sortable: false,
    class: 'text',
    externalLink: (row: UrlRow) => row.url,
    rowTooltip: (row: UrlRow) => urlErrors.value[row.key] || '',
  },
]);
const fields = computed(() => (listType.value === 'wallet' ? walletFields : urlFields.value));
const tableData = computed(() => (listType.value === 'wallet' ? blacklist.value : urlRows.value));
const selectedRows = computed(() => tableData.value.filter((row) => row.disabled).map((row) => row.key));
const i18nPath = computed(() => (
  listType.value === 'wallet' ? 'mediaBuying.blacklistWallets' : 'mediaBuying.blacklistUrls'
));
//#endregion

//#region methods
function getRowClass(row: { key: string }) {
  return urlErrors.value[row.key] ? 'post-blacklist-popup__row_error' : '';
}

function open() {
  isVisible.value = true;
  isLoading.value = true;
  listType.value = 'wallet';
  selected.value = [];
  urlErrors.value = {};

  urlRows.value = props.urls
    .filter((el) => el.url.trim())
    .map((el) => ({
      ...el,
      key: el.url,
      disabled: el.is_blocked,
    }));

  api.getMediaBuyingUsedWalletsById(props.id).then((res) => {
    blacklist.value = res.data.payload.map((el) => ({
      ...el,
      disabled: el.is_blacklisted,
      key: `${el.site_payment_method_id}:${el.payment_wallet}`,
    }));
  }).finally(() => {
    isLoading.value = false;
  });
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSave() {
  if (!selected.value.length) {
    handleClose();
    return;
  }

  isLoading.value = true;
  urlErrors.value = {};
  const sentUrls = [...selected.value];
  const request = listType.value === 'wallet'
    ? api.postMediaBuyingBlacklist({
      blacklist: sentUrls
        .map((key) => blacklist.value.find((el) => el.key === key)!)
        .map((el) => ({
          media_buying_payment_method_id: el.site_payment_method_id!,
          payment_wallet: el.payment_wallet!,
        })),
    })
    : api.postMediaBuyingBlacklistUrls({ urls: sentUrls });

  request.then(() => {
    emit('success');
    handleClose();
  }).catch((error) => {
    if (listType.value !== 'url' || !typeGuard<ErrorResponse<FormErrors>>(error)) return;

    const urls = error.data?.errors?.urls;
    if (!Array.isArray(urls)) return;

    urlErrors.value = urls.reduce<Record<string, string>>((acc, item, index) => {
      const url = sentUrls[index];
      const message = getErrorMessage(item);

      if (url && message) {
        acc[url] = message;
      }

      return acc;
    }, {});
  }).finally(() => isLoading.value = false);
}

function checkedRows(_id: string, ids: string[]) {
  const blocked = new Set(selectedRows.value);
  selected.value = ids.filter((id) => !blocked.has(id));
}
//#endregion

//#region watch
watch(listType, () => {
  selected.value = [];
  urlErrors.value = {};
});
//#endregion
defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="post-blacklist-popup"
    :title="$t('mediaBuying.other.add_to_blacklist')"
    :width="650"
    @close="handleClose"
  >
    <template v-slot:body>
      <CardTitle
        class="t:mb-16"
        :title="$t('mediaBuying.blacklistWallets.popup.type')"
      >
        <UiRadioGroup
          v-model="listType"
          :options="typeOptions"
        />
      </CardTitle>
      <UiTableNew
        :key="listType"
        v-loading="isLoading"
        :select-row="true"
        :column-checkbox-width="34"
        :external-height="512"
        :fields="fields"
        :data="tableData"
        :offset-column="2"
        :sort="{
          prop: sortProp,
          order: sortOrder,
        }"
        :padding="0"
        :default-selected-rows="selectedRows"
        checked-row
        checked-row-id="key"
        is-static
        :i18n-path="i18nPath"
        :computed-row-class="getRowClass"
        element-loading-custom-class="data-loader-spinner"
        @checked-rows="checkedRows"
      />
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isLoading"
          @click="handleSave"
        >
          {{ $t('crm.buttons.confirm') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.post-blacklist-popup {
  .ui-table .el-table &__row_error td.ui-table__table-cell,
  .ui-table .el-table &__row_error:hover td.ui-table__table-cell {
    background-color: var(--background-negative-subtle) !important;
  }
}
</style>
